// Purpose: the authenticated app sidebar. 240px fixed rail with:
//   Brand  — gradient logo box + Apogee/Secure Transfer wordmark
//   Nav    — flat list of items, optionally grouped into sections with
//            a small uppercase label; active item shows accent-muted bg
//            + accent-primary 3px left border
//   User   — UserMenu at the bottom (initials + name + role + kebab
//            for theme toggle / sign out)
//
// Used by AppShell. Static width (no collapse) per Phase 7.5 scope.

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { PadlockIcon } from '../icons/index.tsx';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
  /** Optional badge (number or node) rendered on the right of the item. */
  badge?: ReactNode;
}

export interface SidebarSection {
  /** Optional section header (e.g., "Workspace", "Admin"). */
  label?: string;
  items: SidebarNavItem[];
}

export interface SidebarProps {
  sections: SidebarSection[];
  /** Rendered at the very bottom (UserMenu). */
  footer?: ReactNode;
  className?: string;
}

export function Sidebar({ sections, footer, className }: SidebarProps) {
  return (
    <aside
      aria-label="Primary"
      className={[
        'w-[240px] flex flex-col flex-shrink-0',
        'bg-bg-secondary border-r border-border-default',
        className ?? '',
      ].join(' ')}
    >
      <SidebarBrand />
      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-6">
        {sections.map((section, idx) => (
          <div key={section.label ?? `section-${idx}`} className="flex flex-col gap-1">
            {section.label ? (
              <span className="text-eyebrow uppercase text-fg-muted px-3 mb-2">
                {section.label}
              </span>
            ) : null}
            {section.items.map((item) => (
              <SidebarItem key={item.to} {...item} />
            ))}
          </div>
        ))}
      </nav>
      {footer ? <div className="border-t border-border-default p-3">{footer}</div> : null}
    </aside>
  );
}

function SidebarBrand() {
  return (
    <div className="flex items-center gap-3 h-20 px-5 border-b border-border-default">
      <span
        aria-hidden
        className={[
          'flex items-center justify-center',
          'h-9 w-9 rounded-md text-white',
          'bg-[linear-gradient(135deg,#4F8CFF_0%,#6366F1_50%,#8B5CF6_100%)]',
          'shadow-logo-box',
        ].join(' ')}
      >
        <PadlockIcon size={20} strokeWidth={2.2} />
      </span>
      <span className="flex flex-col gap-[2px] leading-none">
        <span className="text-[13px] font-semibold tracking-[0.02em] text-fg-primary">Apogee</span>
        <span className="text-eyebrow uppercase text-fg-muted">Secure Transfer</span>
      </span>
    </div>
  );
}

function SidebarItem({ to, label, icon, end, badge }: SidebarNavItem) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 h-10 rounded-md',
          'text-body-m transition-colors duration-fast ease-out',
          isActive
            ? 'bg-accent-muted text-fg-primary border-l-[3px] border-accent-primary pl-[9px]'
            : 'text-fg-secondary hover:bg-bg-tertiary hover:text-fg-primary',
        ].join(' ')
      }
    >
      {icon ? <span className="flex-shrink-0 text-fg-muted w-4 h-4">{icon}</span> : null}
      <span className="flex-1 truncate">{label}</span>
      {badge ? <span className="flex-shrink-0">{badge}</span> : null}
    </NavLink>
  );
}
